import { DataLayer, type DataLayerEventData } from "@/lib/dataLayer"
import { type OnboardingFormValues } from "../../wizard/types/onboardingFormValues"
import { OnboardingDataLayerEvent } from "../types/onboardingDataLayerEvent"
import { formatFunnelEnergyType } from "../utils/formatFunnelEnergyType"
import { formatJourneyType } from "../utils/formatJourneyType"
import { getOctoshieldEventData } from "../utils/getOctoshieldEventData"
import { getProductVariant } from "../utils/getProductVariant"
import { getQuoteEventData } from "../utils/getQuoteEventData"

const INITIAL_TIMESTAMP_STORAGE_KEY = "onboarding-initial-timestamp"

const CLOSE_EVENT = OnboardingDataLayerEvent.SubscriptionCompleted

export class OnboardingDataLayer extends DataLayer {
    private initialTimestamp: number | null = null

    constructor() {
        super()

        this.initialTimestamp = this.getInitialTimestamp() || this.generateInitialTimestamp()
    }

    /**
     * Generates a unique initial timestamp by the current timestamp. Store it in sessionStorage in client side only.
     * Return the generated initial timestamp.
     */
    private generateInitialTimestamp() {
        const initialTimestamp = Date.now()

        if (typeof window !== "undefined" && typeof sessionStorage !== "undefined") {
            sessionStorage.setItem(INITIAL_TIMESTAMP_STORAGE_KEY, initialTimestamp.toString())
        }

        return initialTimestamp
    }

    /**
     * Retrieves the initial timestamp from sessionStorage in client side only.
     * Return the initial timestamp or null if not found.
     */
    private getInitialTimestamp() {
        if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
            return null
        }

        const initialTimestamp = sessionStorage.getItem(INITIAL_TIMESTAMP_STORAGE_KEY)

        if (!initialTimestamp) {
            return null
        }

        return parseInt(initialTimestamp, 10)
    }

    /**
     * Calculates the duration of the session based on the initial timestamp and the current time.
     * Return the session duration in seconds or undefined if the initial timestamp is not set.
     */
    private getSessionDuration() {
        if (!this.initialTimestamp) {
            return undefined
        }

        return Math.floor((Date.now() - this.initialTimestamp) / 1000)
    }

    public sendTrackingEvent(
        event: OnboardingDataLayerEvent,
        formValues: OnboardingFormValues,
        data?: DataLayerEventData
    ) {
        const partner = (formValues.partnerSlug ?? "OCTOPUS").toUpperCase()

        this.sendDataLayerEvent(event, {
            partner,
            funnel: formValues.affiliateOrganisationSalesChannel,
            affiliate_code: formValues.affiliateCode || undefined,
            input_method: formValues.inputMethod,
            is_cross_sale: !!formValues.propertyId,
            product: formatFunnelEnergyType(formValues.selectedEnergyTypes),
            subscription_type: formatJourneyType(formValues.journeyType),
            subscription_funnel_variant: formValues.subscriptionType,
            quote_variant_funnel: formValues.featureFlags.quoteVariant,
            product_variant: getProductVariant(formValues.affiliateCode),
            ...getQuoteEventData(event, formValues),
            ...getOctoshieldEventData(event, formValues),
            ...data,
        })
    }

    public closeTrackingSession(formValues: OnboardingFormValues, data?: DataLayerEventData) {
        this.sendTrackingEvent(CLOSE_EVENT, formValues, {
            ...data,
            open_banking: formValues.riskAssessment ?? "NA",
            duration: this.getSessionDuration(),
            transaction_id: formValues.quoteCode,
            join_supplier_number: formValues.joinSupplierProcessNumber || "",
        })

        sessionStorage.removeItem(INITIAL_TIMESTAMP_STORAGE_KEY)
    }
}
